import { Label } from "@convin/components/custom_components/Typography/Label";
import {
    useGetAllResourcesQuery,
    useGetLmsResourcesQuery,
} from "@convin/redux/services/lms/lmsKnowledgeBase.service";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import {
    alpha,
    Box,
    Chip,
    Dialog,
    DialogTitle,
    Divider,
    Grid2,
    Typography,
    useTheme,
} from "@mui/material";
import { CloseSvg } from "@convin/components/svg";
import { pxToRem } from "@convin/utils/getFontValue";
import AttachLmsDocumentsProvider from "./context/AttachLmsDocumentsProvider";
import CustomSkeletonLoader from "@convin/components/custom_components/Skeleton";
import FolderSelectionUi from "./FolderSelectionUi";
import { useState } from "react";
import { IAttachLmsDocumentsContext } from "./context/AttachLmsDocumentsContext";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";

const CustomChip: React.FC<{
    name: string;
    type: "file" | "folder";
    onDelete: () => void;
}> = ({ name, type, onDelete }) => {
    const theme = useTheme();

    return (
        <Grid2
            size={{ xs: 4 }}
            component={Chip}
            sx={{
                "&.MuiButtonBase-root": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                },
                "&.MuiChip-root": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
                "& .MuiSvgIcon-root": {
                    color: theme.palette.primary.main,
                },
            }}
            color="primary"
            label={
                <span className="flex items-center gap-1">
                    {type === "file" ? (
                        <InsertDriveFileOutlinedIcon
                            sx={{ fontSize: pxToRem(16) }}
                        />
                    ) : (
                        <FolderOutlinedIcon sx={{ fontSize: pxToRem(16) }} />
                    )}
                    <Label
                        hasTooltip
                        isEllipses
                        colorType="primary"
                        variant="small"
                    >
                        {name}
                    </Label>
                </span>
            }
            onDelete={onDelete}
        />
    );
};

export default function AttachLmsDocuments({
    handleSave,
    initialValue,
}: {
    handleSave: (value: IAttachLmsDocumentsContext["value"]) => void;
    initialValue: IAttachLmsDocumentsContext["value"];
}) {
    const [open, setOpen] = useState(false);
    const onClose = () => {
        setOpen(false);
    };
    const { data: folderList, isLoading: isFolderListLoading } =
        useGetLmsResourcesQuery(undefined, {
            skip: initialValue.folders.length === 0,
        });
    const { data: documents, isLoading: isLmsDocsLoading } =
        useGetAllResourcesQuery(
            {
                file_ext_type: [".csv", ".docx", ".md", ".pdf", ".json"],
                allow_pagination: false,
            },
            {
                skip: initialValue.files.length === 0,
            }
        );

    return (
        <>
            <Box
                onClick={() => setOpen(true)}
                component="button"
                className="block text-left"
                sx={{
                    fontSize: pxToRem(14),
                    my: 2,
                }}
                color="primary.main"
                type="button"
            >
                <AttachFileIcon
                    sx={{
                        fontSize: pxToRem(20),
                        transform: "rotate(45deg)",
                    }}
                />
                Attach files from LMS
            </Box>
            {isLmsDocsLoading || isFolderListLoading ? (
                <CustomSkeletonLoader rows={1} />
            ) : (
                <Grid2
                    container
                    spacing={1}
                    sx={{ mb: initialValue.folders.length > 0 ? 2 : 0 }}
                >
                    {documents
                        ?.filter((e) => initialValue.files.includes(e.id))
                        .map((e) => (
                            <CustomChip
                                key={e.id}
                                name={`${e.filename}.${e.file_type}`}
                                type="file"
                                onDelete={() => {
                                    handleSave({
                                        files: initialValue.files.filter(
                                            (id) => id !== e.id
                                        ),
                                        folders: initialValue.folders,
                                    });
                                }}
                            />
                        ))}
                    {folderList?.results
                        ?.filter((e) => initialValue.folders.includes(e.id))
                        .map((e) => (
                            <CustomChip
                                key={e.id}
                                name={e.name}
                                type="folder"
                                onDelete={() => {
                                    handleSave({
                                        files: initialValue.files,
                                        folders: initialValue.folders.filter(
                                            (id) => id !== e.id
                                        ),
                                    });
                                }}
                            />
                        ))}
                </Grid2>
            )}
            {open && (
                <AttachLmsDocumentsProvider
                    initialValue={initialValue}
                    handleSave={handleSave}
                >
                    <Dialog
                        open={open}
                        onClose={onClose}
                        sx={{
                            "& .MuiDialog-paper": {
                                width: "1134px",
                                maxWidth: "1134px",
                                borderRadius: "12px",
                            },
                        }}
                        className="flex flex-col"
                    >
                        <DialogTitle
                            className="flex items-center justify-between flex-shrink-0"
                            sx={{ px: 3, py: 2.5 }}
                            component="div"
                        >
                            <Typography variant="h4">
                                Choose Folders or Files
                            </Typography>
                            <button onClick={onClose} type="button">
                                <CloseSvg />
                            </button>
                        </DialogTitle>
                        <Divider />
                        <FolderSelectionUi open={open} onClose={onClose} />
                        {/* <UI handleUpload={handleUpload} onClose={onClose} /> */}
                    </Dialog>
                </AttachLmsDocumentsProvider>
            )}
        </>
    );
}
